function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { SYNC_PROTOCOL_VERSION } from './types';
/**
 * Entities per envelope. Structured-cloning one huge array blocks the RECEIVING
 * tab's main thread for the whole clone; chunking bounds that pause. Total work
 * is unchanged — it is the per-message stall we are capping.
 */ const MAX_ENTITIES_PER_ENVELOPE = 200;
/**
 * How long a tab holds outbound data while it knows of no peers.
 *
 * Covers the handshake race: another tab can be opening when a mutation
 * commits here, and its `hello` may not have landed yet. Dropping data in that
 * window loses the change permanently — the newcomer never hears about it and
 * nothing replays it.
 *
 * Short on purpose. Past this, "no peers" means genuinely alone, and a tab
 * opening later loads its own data anyway, so replaying an old edit at it is
 * pointless.
 */ const HANDSHAKE_GRACE_MS = 1000;
/** Ceiling on messages held during that window — a bulk edit must not pin an
 *  unbounded amount of memory in a tab that turns out to be alone. */ const MAX_HELD_MESSAGES = 200;
const randomId = ()=>{
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') {
        return c.randomUUID();
    }
    // Only reached in environments without WebCrypto (old test runners). Tab ids
    // just need to be distinct among concurrently-open tabs, not unguessable.
    return `tab-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
};
/**
 * Cross-tab transport for state-db.
 *
 * Owns exactly one BroadcastChannel and knows nothing about collections — it
 * moves {@link SyncMessage}s between same-origin tabs, enforces the identity
 * guard, batches outbound traffic, and tracks peers. Collection wiring lives in
 * `attachTabSync`.
 *
 * No-ops entirely (every method safe to call) where BroadcastChannel is
 * unavailable.
 */ export class TabSync {
    /** Build the channel and wire receive. Returns false if unavailable. */ openChannel() {
        try {
            this.channel = new BroadcastChannel(this.options.channelName);
        } catch (error) {
            var _this_options_onError, _this_options;
            (_this_options_onError = (_this_options = this.options).onError) === null || _this_options_onError === void 0 ? void 0 : _this_options_onError.call(_this_options, error, 'construct');
            return false;
        }
        this.channel.onmessage = (event)=>this.receive(event.data);
        this.graceUntil = Date.now() + HANDSHAKE_GRACE_MS;
        return true;
    }
    /** Release the channel for a bfcache freeze, keeping the instance usable. */ suspend() {
        if (this.closed || this.suspended) {
            return;
        }
        this.suspended = true;
        if (this.flushTimer !== null) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        try {
            this.send([
                {
                    t: 'bye'
                }
            ]);
        } catch (error) {
            var _this_options_onError, _this_options;
            (_this_options_onError = (_this_options = this.options).onError) === null || _this_options_onError === void 0 ? void 0 : _this_options_onError.call(_this_options, error, 'bye');
        }
        try {
            var _this_channel;
            (_this_channel = this.channel) === null || _this_channel === void 0 ? void 0 : _this_channel.close();
        } catch (unused) {
        // Already released by the browser.
        }
        this.channel = null;
        // Peers we knew are stale by the time we come back; rediscover via hello.
        this.peers.clear();
        this.peerSeq.clear();
        this.heldData = [];
    }
    /** Rebuild after a bfcache restore. */ resume() {
        if (this.closed || !this.suspended) {
            return;
        }
        this.suspended = false;
        if (typeof BroadcastChannel === 'undefined' || !this.openChannel()) {
            return;
        }
        this.post({
            t: 'hello'
        });
    }
    get isEnabled() {
        return this.channel !== null && !this.closed;
    }
    /** Number of other tabs known to be listening. */ get peerCount() {
        return this.peers.size;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return ()=>{
            this.listeners.delete(listener);
        };
    }
    /** Queue a message. Flushed with everything else queued this tick. */ post(msg) {
        if (!this.isEnabled) {
            return;
        }
        this.outbox.push(msg);
        if (this.flushTimer !== null) {
            return;
        }
        // Macrotask, not rAF: a hidden tab gets no frames, and an edit made just
        // before switching away must still reach its peers.
        this.flushTimer = setTimeout(()=>this.flush(), 0);
    }
    flush() {
        this.flushTimer = null;
        const queued = this.outbox;
        this.outbox = [];
        if (!this.isEnabled || queued.length === 0) {
            return;
        }
        const presence = queued.filter((m)=>m.t === 'hello' || m.t === 'bye');
        const data = queued.filter((m)=>m.t !== 'hello' && m.t !== 'bye');
        // Presence must go out even with no known peers — that is how peers become
        // known. Data must not: cloning a payload nobody will read is pure cost.
        //
        // But "no peers known" is not the same as "no peers": a tab opening right
        // now may not have said hello yet. Within the grace window the data is HELD
        // rather than dropped, and replayed the moment a peer appears.
        if (this.peers.size === 0) {
            if (data.length > 0 && Date.now() < this.graceUntil) {
                this.heldData.push(...data);
                if (this.heldData.length > MAX_HELD_MESSAGES) {
                    this.heldData.splice(0, this.heldData.length - MAX_HELD_MESSAGES);
                }
            }
            if (presence.length > 0) {
                this.chunk(presence).forEach((msgs)=>this.send(msgs));
            }
            return;
        }
        const sendable = [
            ...presence,
            ...data
        ];
        if (sendable.length === 0) {
            return;
        }
        this.chunk(sendable).forEach((msgs)=>this.send(msgs));
    }
    /** Split so no envelope exceeds {@link MAX_ENTITIES_PER_ENVELOPE} entities. */ chunk(msgs) {
        const batches = [];
        let current = [];
        let count = 0;
        const push = (msg, size)=>{
            if (count + size > MAX_ENTITIES_PER_ENVELOPE && current.length > 0) {
                batches.push(current);
                current = [];
                count = 0;
            }
            current.push(msg);
            count += size;
        };
        msgs.forEach((msg)=>{
            if (msg.t !== 'upsert') {
                push(msg, 0);
                return;
            }
            // A single upsert can itself exceed the cap — split its entity list.
            for(let i = 0; i < msg.entities.length; i += MAX_ENTITIES_PER_ENVELOPE){
                const slice = msg.entities.slice(i, i + MAX_ENTITIES_PER_ENVELOPE);
                push({
                    ...msg,
                    entities: slice
                }, slice.length);
            }
        });
        if (current.length > 0) {
            batches.push(current);
        }
        return batches;
    }
    send(msgs) {
        const { accountId, userId } = this.options.getIdentity();
        this.outSeq += 1;
        const envelope = {
            v: SYNC_PROTOCOL_VERSION,
            sender: this.senderId,
            seq: this.outSeq,
            accountId,
            userId,
            msgs
        };
        try {
            var _this_channel;
            (_this_channel = this.channel) === null || _this_channel === void 0 ? void 0 : _this_channel.postMessage(envelope);
        } catch (error) {
            var // Most likely a non-cloneable payload (a class instance, a function on an
            // entity). Report it — silently dropping would look like a sync bug.
            _this_options_onError, _this_options;
            (_this_options_onError = (_this_options = this.options).onError) === null || _this_options_onError === void 0 ? void 0 : _this_options_onError.call(_this_options, error, 'postMessage');
        }
    }
    receive(envelope) {
        var _this_peerSeq_get;
        if (!envelope || envelope.v !== SYNC_PROTOCOL_VERSION) {
            return;
        }
        // Own echo. BroadcastChannel does not deliver to the posting context, but
        // two channel objects in ONE tab do see each other — so check explicitly.
        if (envelope.sender === this.senderId) {
            return;
        }
        // Identity guard, evaluated against the CURRENT session on both sides. A
        // tab sitting on a stale login must not accept — or be trusted with —
        // another principal's records.
        const { accountId, userId } = this.options.getIdentity();
        if (envelope.accountId !== accountId || envelope.userId !== userId) {
            return;
        }
        const lastSeq = (_this_peerSeq_get = this.peerSeq.get(envelope.sender)) !== null && _this_peerSeq_get !== void 0 ? _this_peerSeq_get : 0;
        if (envelope.seq <= lastSeq) {
            return;
        }
        this.peerSeq.set(envelope.sender, envelope.seq);
        const forwarded = [];
        envelope.msgs.forEach((msg)=>{
            if (msg.t === 'hello') {
                const isNew = !this.peers.has(envelope.sender);
                this.peers.add(envelope.sender);
                // Reply so the newcomer learns about us too — otherwise whoever opened
                // first has peerCount 0 and never sends data.
                if (isNew) {
                    this.post({
                        t: 'hello'
                    });
                    this.replayHeldData();
                }
                return;
            }
            if (msg.t === 'bye') {
                this.peers.delete(envelope.sender);
                this.peerSeq.delete(envelope.sender);
                return;
            }
            forwarded.push(msg);
        });
        if (forwarded.length === 0) {
            return;
        }
        this.listeners.forEach((fn)=>{
            try {
                fn(forwarded, envelope.sender);
            } catch (error) {
                var // One collection's apply must not stop the others'.
                _this_options_onError, _this_options;
                (_this_options_onError = (_this_options = this.options).onError) === null || _this_options_onError === void 0 ? void 0 : _this_options_onError.call(_this_options, error, 'apply');
            }
        });
    }
    /**
   * Send whatever was withheld before this tab knew of any peer.
   *
   * Ordering holds: these were queued before anything sent after the handshake,
   * and they go out first.
   */ replayHeldData() {
        if (this.heldData.length === 0) {
            return;
        }
        const held = this.heldData;
        this.heldData = [];
        this.chunk(held).forEach((msgs)=>this.send(msgs));
    }
    close() {
        if (this.closed) {
            return;
        }
        // Send `bye` synchronously — a queued flush will not survive teardown.
        if (this.channel) {
            try {
                this.send([
                    {
                        t: 'bye'
                    }
                ]);
            } catch (error) {
                var _this_options_onError, _this_options;
                (_this_options_onError = (_this_options = this.options).onError) === null || _this_options_onError === void 0 ? void 0 : _this_options_onError.call(_this_options, error, 'bye');
            }
        }
        this.closed = true;
        if (this.flushTimer !== null) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        if (typeof window !== 'undefined') {
            window.removeEventListener('pagehide', this.onPageHide);
            window.removeEventListener('pageshow', this.onPageShow);
        }
        try {
            var _this_channel;
            (_this_channel = this.channel) === null || _this_channel === void 0 ? void 0 : _this_channel.close();
        } catch (unused) {
        // Already closed by the browser during teardown — nothing to release.
        }
        this.channel = null;
        this.listeners.clear();
        this.peers.clear();
        this.peerSeq.clear();
        this.heldData = [];
    }
    constructor(options){
        _define_property(this, "senderId", randomId());
        _define_property(this, "channel", null);
        _define_property(this, "options", void 0);
        _define_property(this, "outSeq", 0);
        /** Highest `seq` applied per peer — drops envelopes that arrive out of order. */ _define_property(this, "peerSeq", new Map());
        /** Peers seen via presence. Empty ⇒ nobody to talk to ⇒ skip data sends. */ _define_property(this, "peers", new Set());
        _define_property(this, "outbox", []);
        /** Data withheld while no peer is known yet. See {@link HANDSHAKE_GRACE_MS}. */ _define_property(this, "heldData", []);
        /** Epoch-ms until which {@link heldData} is worth keeping. */ _define_property(this, "graceUntil", 0);
        _define_property(this, "flushTimer", null);
        _define_property(this, "listeners", new Set());
        _define_property(this, "closed", false);
        /** Torn down for bfcache, not for good — see {@link onPageHide}. */ _define_property(this, "suspended", false);
        /**
   * `pagehide` fires on BOTH a real teardown and a bfcache navigation, and the
   * two need opposite handling.
   *
   * On bfcache the page is frozen, not destroyed: coming back resumes the same
   * JS context, the same React tree and the same module singleton. Treating
   * that as a permanent close left `getTabSync()` handing out a dead instance
   * for the rest of the page's life — no mount effect re-runs to rebuild it, so
   * sync stayed silently off after any back-button navigation.
   *
   * So: release the channel either way (a frozen page must not hold one), but
   * remember which case it was and rebuild on `pageshow`.
   */ _define_property(this, "onPageHide", (event)=>{
            if (event === null || event === void 0 ? void 0 : event.persisted) {
                this.suspend();
                return;
            }
            this.close();
        });
        _define_property(this, "onPageShow", (event)=>{
            if (event === null || event === void 0 ? void 0 : event.persisted) {
                this.resume();
            }
        });
        this.options = options;
        if (typeof BroadcastChannel === 'undefined') {
            return;
        }
        if (!this.openChannel()) {
            return;
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('pagehide', this.onPageHide);
            window.addEventListener('pageshow', this.onPageShow);
        }
        this.post({
            t: 'hello'
        });
    }
}
